import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

// Suppress invalid refresh token errors thrown internally by Supabase auth-js
// These are handled gracefully via onAuthStateChange SIGNED_OUT event
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const msg = event?.reason?.message || '';
    if (
      msg?.includes('Invalid Refresh Token') ||
      msg?.includes('Refresh Token Not Found') ||
      msg?.includes('refresh_token_not_found')
    ) {
      event?.preventDefault(); // Suppress the console error
      // Clear stale session keys from localStorage
      try {
        Object.keys(localStorage)?.forEach(key => {
          if (key?.startsWith('sb-') && key?.includes('-auth-token')) {
            localStorage.removeItem(key);
          }
        });
      } catch (_) {}
    }
  });
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    retryAttempts: 0,
  },
  global: {
    headers: {
      'x-client-info': 'supabase-js-web',
    },
    fetch: async (url, options = {}) => {
      try {
        // Add timeout to fetch requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller?.abort(), 30000); // 30 second timeout
        
        const response = await fetch(url, {
          ...options,
          signal: controller?.signal,
        });
        
        clearTimeout(timeoutId);
        
        // Handle auth errors in response
        if (!response?.ok && response?.status === 401) {
          const errorData = await response?.clone()?.json()?.catch(() => ({}));
          if (errorData?.message?.includes('Invalid Refresh Token') || errorData?.message?.includes('Refresh Token Not Found')) {
            console.warn('Invalid refresh token detected in response, clearing local session...');
            clearSupabaseSession();
          }
          // Emit global 401 event for toast notifications
          window.dispatchEvent(new CustomEvent('app:auth-error', { detail: { status: 401 } }));
        }

        if (!response?.ok && response?.status === 403) {
          // Emit global 403 event for toast notifications
          window.dispatchEvent(new CustomEvent('app:auth-error', { detail: { status: 403 } }));
        }
        
        return response;
      } catch (error) {
        if (error?.name === 'AbortError') {
          console.error('Request timeout - please check your internet connection');
          throw new Error('Request timeout - please check your internet connection');
        }
        console.error('Network error:', error?.message);
        throw error;
      }
    },
  },
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Helper function to clear Supabase session from localStorage
const clearSupabaseSession = () => {
  try {
    // Supabase stores session with a key pattern like: sb-<project-ref>-auth-token
    const keys = Object.keys(localStorage);
    keys?.forEach(key => {
      if (key?.startsWith('sb-') && key?.includes('-auth-token')) {
        localStorage.removeItem(key);
        console.log('Cleared Supabase session key:', key);
      }
    });
  } catch (error) {
    console.error('Error clearing Supabase session:', error);
  }
};

// Network connectivity check helper
export const checkNetworkConnectivity = async () => {
  // First check browser's built-in online status
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return false;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller?.abort(), 5000);

    // Use a reliable public endpoint that always returns 200 when online.
    // Avoid hitting the Supabase REST API here — it can return 4xx for valid
    // reasons (e.g. missing auth headers) which would falsely flag as offline.
    const response = await fetch('https://www.google.com/generate_204', {
      method: 'HEAD',
      signal: controller?.signal,
      cache: 'no-store',
      mode: 'no-cors', // avoids CORS errors; a successful opaque response means we're online
    });

    clearTimeout(timeoutId);
    // With mode:'no-cors' the response type is 'opaque' and status is 0, but
    // the fetch itself succeeding means we have internet access.
    return true;
  } catch (error) {
    // AbortError = timeout, TypeError = no network
    console.error('Network connectivity check failed:', error?.message);
    return false;
  }
};

// Enhanced error handler for Supabase operations
export const handleSupabaseError = (error, context = '') => {
  console.error(`Supabase error in ${context}:`, error);
  
  // Handle refresh token errors
  if (error?.message?.includes('Invalid Refresh Token') || error?.message?.includes('Refresh Token Not Found')) {
    console.warn('Invalid refresh token detected, user needs to re-authenticate');
    // Clear Supabase session properly
    clearSupabaseSession();
    return {
      error: 'Your session has expired. Please sign in again.',
      code: 'SESSION_EXPIRED',
      shouldSignOut: true,
    };
  }
  
  if (error?.message?.includes('Failed to fetch')) {
    return {
      error: 'Network connection error. Please check your internet connection.',
      code: 'NETWORK_ERROR',
    };
  }
  
  if (error?.message?.includes('timeout')) {
    return {
      error: 'Request timed out. Please try again.',
      code: 'TIMEOUT_ERROR',
    };
  }
  
  // Default error handling
  return {
    error: error?.message || 'An unexpected error occurred',
    code: error?.code || 'UNKNOWN_ERROR',
  };
};

// Global error interceptor for Supabase auth errors
let authErrorCallback = null;

export const setAuthErrorCallback = (callback) => {
  authErrorCallback = callback;
};

export const handleAuthError = async (error) => {
  if (error?.message?.includes('Invalid Refresh Token') || error?.message?.includes('Refresh Token Not Found')) {
    console.warn('Auth error detected: Invalid refresh token');
    
    // Clear all auth-related storage
    try {
      await supabase?.auth?.signOut();
      localStorage?.removeItem('supabase.auth.token');
      
      // Notify the app to update UI
      if (authErrorCallback) {
        authErrorCallback();
      }
    } catch (signOutError) {
      console.error('Error during forced sign out:', signOutError);
    }
    
    return true; // Error was handled
  }
  
  return false; // Error was not handled
};